import type { SessionSettings } from '@/types/session';
import {
  DEFAULT_DISPLAY_DATE_FORMAT,
  DEFAULT_DISPLAY_DATETIME_FORMAT,
  DEFAULT_DISPLAY_TIME_FORMAT,
  normalizeDisplayDateFormat,
  normalizeDisplayDateTimeFormat,
  normalizeDisplayTimeFormat,
  type DisplayDateFormatId,
  type DisplayDateTimeFormatId,
  type DisplayTimeFormatId,
} from '@/utils/displayFormatConstants';
import { parseStoredUtcValue } from '@/utils/displayFormatEngine';

/** Display preferences from session (company timezone / locale / formats). */
export interface DisplayContext {
  timezone: string;
  locale: string;
  dateFormat: DisplayDateFormatId;
  timeFormat: DisplayTimeFormatId;
  dateTimeFormat: DisplayDateTimeFormatId;
}

declare const utcInstantBrand: unique symbol;
declare const calendarDateBrand: unique symbol;
declare const wallTimeBrand: unique symbol;
declare const companyLocalDateTimeBrand: unique symbol;

/** ISO-8601 instant with an explicit UTC `Z` suffix. */
export type UtcInstantIso = string & { readonly [utcInstantBrand]: true };
/** Timezone-neutral Gregorian calendar date (`yyyy-MM-dd`). */
export type CalendarDate = string & { readonly [calendarDateBrand]: true };
/** Timezone-neutral wall time (`HH:mm` or `HH:mm:ss`). */
export type WallTime = string & { readonly [wallTimeBrand]: true };
/** Company-local picker datetime (`yyyy-MM-dd'T'HH:mm`), not an instant. */
export type CompanyLocalDateTime = string & { readonly [companyLocalDateTimeBrand]: true };

export type RelativeDayGroup = 'YESTERDAY' | 'TODAY' | 'TOMORROW' | 'PAST' | 'FUTURE';

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const DEFAULT: DisplayContext = {
  timezone: 'UTC',
  locale: 'en-IN',
  dateFormat: DEFAULT_DISPLAY_DATE_FORMAT,
  timeFormat: DEFAULT_DISPLAY_TIME_FORMAT,
  dateTimeFormat: DEFAULT_DISPLAY_DATETIME_FORMAT,
};

let current: DisplayContext = { ...DEFAULT };

/** Apply session settings after bootstrap / company switch. */
export function applyDisplaySettings(settings?: SessionSettings | null): void {
  const activeTz = settings?.activeTimezone?.trim()
    || settings?.userTimezone?.trim()
    || settings?.timezone?.trim()
    || DEFAULT.timezone;
  current = {
    timezone: activeTz,
    locale: settings?.displayLocale?.trim() || settings?.locale?.trim() || DEFAULT.locale,
    dateFormat: normalizeDisplayDateFormat(settings?.dateFormat),
    timeFormat: normalizeDisplayTimeFormat(settings?.timeFormat),
    dateTimeFormat: normalizeDisplayDateTimeFormat(settings?.dateTimeFormat),
  };
}

/** Reset display context on logout (before next bootstrap). */
export function resetDisplaySettings(): void {
  current = { ...DEFAULT };
}

export function getDisplayTimezone(): string {
  return current.timezone;
}

export function getDisplayLocale(): string {
  return current.locale;
}

export function getDisplayDateFormat(): DisplayDateFormatId {
  return current.dateFormat;
}

export function getDisplayTimeFormat(): DisplayTimeFormatId {
  return current.timeFormat;
}

export function getDisplayDateTimeFormat(): DisplayDateTimeFormatId {
  return current.dateTimeFormat;
}

export function getDisplayContext(): DisplayContext {
  return { ...current };
}

/**
 * Parse API date/time strings as UTC instants.
 * Backend persists timestamps in UTC (naive ISO strings are treated as UTC).
 */
export function parseStoredUtc(value: string): Date {
  return parseStoredUtcValue(value);
}

const TIMEZONE_SHORT_NAME_OVERRIDES: Readonly<Record<string, string>> = {
  // Intl returns "GMT+5:30" for these zones in locales such as en-US.
  // Use the conventional Indian Standard Time name consistently in the UI.
  'Asia/Calcutta': 'IST',
  'Asia/Kolkata': 'IST',
};

/** Short timezone label for the active company (e.g. IST, EDT). */
export function formatTimezoneLabel(instant = new Date()): string {
  return timezoneLabel(getDisplayTimezone(), instant, getDisplayLocale());
}

/** Short label for an IANA timezone at a specific instant. */
export function timezoneLabel(timeZone: string, instant = new Date(), locale = getDisplayLocale()): string {
  const override = TIMEZONE_SHORT_NAME_OVERRIDES[timeZone];
  if (override) return override;

  try {
    const parts = new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(instant);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

function dateTimePartsInZone(instant: Date, timeZone: string): DateTimeParts {
  const map = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((p) => [p.type, p.value]),
  );
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    // hourCycle h23 prevents engines from representing midnight as hour 24.
    hour: Number(map.hour) % 24,
    minute: Number(map.minute),
    second: Number(map.second),
  };
}

function timezoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = dateTimePartsInZone(instant, timeZone);
  const instantToSecond = Math.trunc(instant.getTime() / 1000) * 1000;
  return Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  ) - instantToSecond;
}

function parseCalendarParts(value: string): Pick<DateTimeParts, 'year' | 'month' | 'day'> {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) throw new RangeError(`Invalid CalendarDate: ${value}`);
  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year
    || probe.getUTCMonth() !== month - 1
    || probe.getUTCDate() !== day
  ) {
    throw new RangeError(`Invalid CalendarDate: ${value}`);
  }
  return { year, month, day };
}

function parseCompanyLocalParts(value: string): DateTimeParts {
  const normalized = value.includes('T') ? value.trim() : `${value.trim()}T00:00:00`;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(normalized);
  if (!match) throw new RangeError(`Invalid company-local datetime: ${value}`);
  const [, y, mo, d, h, mi, s = '00'] = match;
  const date = parseCalendarParts(`${y}-${mo}-${d}`);
  const hour = Number(h);
  const minute = Number(mi);
  const second = Number(s);
  if (hour > 23 || minute > 59 || second > 59) {
    throw new RangeError(`Invalid company-local datetime: ${value}`);
  }
  return { ...date, hour, minute, second };
}

export function asCalendarDate(value: string): CalendarDate {
  const parts = parseCalendarParts(value);
  return calendarDateKeyFromParts(parts.year, parts.month, parts.day);
}

export function asWallTime(value: string): WallTime {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59 || Number(match[3] ?? 0) > 59) {
    throw new RangeError(`Invalid WallTime: ${value}`);
  }
  return value.trim() as WallTime;
}

export function asCompanyLocalDateTime(value: string): CompanyLocalDateTime {
  const parts = parseCompanyLocalParts(value);
  return `${calendarDateKeyFromParts(parts.year, parts.month, parts.day)}T${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}` as CompanyLocalDateTime;
}

/** Validate a canonical UTC instant. Legacy offset-less UTC parsing remains available via parseStoredUtc. */
export function asUtcInstant(value: string): UtcInstantIso {
  if (!/Z$/i.test(value.trim())) throw new RangeError(`UTC instant must end in Z: ${value}`);
  const parsed = parseStoredUtcValue(value);
  if (Number.isNaN(parsed.getTime())) throw new RangeError(`Invalid UTC instant: ${value}`);
  return parsed.toISOString() as UtcInstantIso;
}

function sameDateTimeParts(left: DateTimeParts, right: DateTimeParts): boolean {
  return left.year === right.year
    && left.month === right.month
    && left.day === right.day
    && left.hour === right.hour
    && left.minute === right.minute
    && left.second === right.second;
}

/** Resolve a company wall datetime, rejecting DST gaps and choosing the earlier instant in overlaps. */
export function companyLocalDateTimeToInstant(
  localValue: string,
  timeZone = getDisplayTimezone(),
): Date {
  const local = parseCompanyLocalParts(localValue);
  const wallUtc = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second,
  );
  // Offsets immediately around the local date contain both sides of an overlap.
  const offsets = new Set<number>();
  for (let hours = -36; hours <= 36; hours += 6) {
    offsets.add(timezoneOffsetMs(new Date(wallUtc + hours * 3_600_000), timeZone));
  }
  const candidates = [...offsets]
    .map((offset) => new Date(wallUtc - offset))
    .filter((candidate) => sameDateTimeParts(dateTimePartsInZone(candidate, timeZone), local))
    .sort((left, right) => left.getTime() - right.getTime());
  if (candidates.length === 0) {
    throw new RangeError(`Nonexistent local datetime ${localValue} in timezone ${timeZone}`);
  }
  // The first candidate uses the offset before the transition (the documented earlier-offset policy).
  return candidates[0];
}

/** Convert a company-local picker value to a canonical UTC instant string. */
export function companyPickerToUtcInstant(
  localValue: string,
  timeZone = getDisplayTimezone(),
): UtcInstantIso {
  return companyLocalDateTimeToInstant(localValue, timeZone).toISOString() as UtcInstantIso;
}

/**
 * Convert a company-local datetime picker value to UTC ISO string for API payloads.
 */
export function toUtcIsoStringFromCompanyLocal(localIso: string): string {
  return companyPickerToUtcInstant(localIso);
}

/** Format Y-M-D parts as yyyy-MM-dd (timezone-neutral calendar date). */
export function calendarDateKeyFromParts(year: number, month: number, day: number): CalendarDate {
  const value = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  parseCalendarParts(value);
  return value as CalendarDate;
}

/**
 * Calendar date key from a grid Date built via `new Date(y, m, d)` or date-fns.
 * Uses local Y-M-D components so browser vs company timezone cannot shift the day.
 */
export function calendarDateKeyFromWallDate(date: Date): CalendarDate {
  if (Number.isNaN(date.getTime())) throw new RangeError('Invalid wall Date');
  return calendarDateKeyFromParts(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** Add calendar days to a yyyy-MM-dd key without timezone conversion. */
export function addCalendarDaysToKey(dateKey: string, days: number): CalendarDate {
  const { year, month, day } = parseCalendarParts(dateKey);
  const result = new Date(Date.UTC(year, month - 1, day + days));
  return calendarDateKeyFromParts(result.getUTCFullYear(), result.getUTCMonth() + 1, result.getUTCDate());
}

/** Add calendar months, clamping to the final valid day of the target month. */
export function addCalendarMonthsToKey(dateKey: string, months: number): CalendarDate {
  const { year, month, day } = parseCalendarParts(dateKey);
  const targetFirst = new Date(Date.UTC(year, month - 1 + months, 1));
  const finalDay = new Date(Date.UTC(
    targetFirst.getUTCFullYear(),
    targetFirst.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  return calendarDateKeyFromParts(
    targetFirst.getUTCFullYear(),
    targetFirst.getUTCMonth() + 1,
    Math.min(day, finalDay),
  );
}

/** Whole Gregorian calendar-day difference between two date keys. */
export function calendarDayDifference(startDateKey: string, endDateKey: string): number {
  const start = parseCalendarParts(startDateKey);
  const end = parseCalendarParts(endDateKey);
  return Math.round((
    Date.UTC(end.year, end.month - 1, end.day)
    - Date.UTC(start.year, start.month - 1, start.day)
  ) / 86_400_000);
}

/** Calendar date at an instant in an explicit IANA timezone. */
export function calendarDateAtInstant(
  instant: Date | string,
  timeZone = getDisplayTimezone(),
): CalendarDate {
  const date = typeof instant === 'string' ? parseStoredUtcValue(instant) : instant;
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid UTC instant: ${String(instant)}`);
  const parts = dateTimePartsInZone(date, timeZone);
  return calendarDateKeyFromParts(parts.year, parts.month, parts.day);
}

/** Calendar date key (yyyy-MM-dd) in the active display timezone. */
export function dateKeyInDisplayTimezone(instant: Date | string): CalendarDate | '' {
  try {
    return calendarDateAtInstant(instant);
  } catch {
    return '';
  }
}

/** Today's date (yyyy-MM-dd) in the active display timezone. */
export function todayInDisplayTimezone(now = new Date()): CalendarDate {
  return calendarDateAtInstant(now);
}

/** Relative calendar-day bucket in company time, independent of browser timezone. */
export function relativeDayGroup(
  value: Date | string,
  now = new Date(),
  timeZone = getDisplayTimezone(),
): RelativeDayGroup {
  const key = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value
    : calendarDateAtInstant(value, timeZone);
  const today = calendarDateAtInstant(now, timeZone);
  const parsed = parseCalendarParts(key);
  const todayParsed = parseCalendarParts(today);
  const difference = Math.round((
    Date.UTC(parsed.year, parsed.month - 1, parsed.day)
    - Date.UTC(todayParsed.year, todayParsed.month - 1, todayParsed.day)
  ) / 86_400_000);
  if (difference === -1) return 'YESTERDAY';
  if (difference === 0) return 'TODAY';
  if (difference === 1) return 'TOMORROW';
  return difference < 0 ? 'PAST' : 'FUTURE';
}

/**
 * Convert UTC ISO from API to company-local picker value (yyyy-MM-dd'T'HH:mm).
 */
export function utcInstantToCompanyPicker(
  iso: string | null | undefined,
  timeZone = getDisplayTimezone(),
): CompanyLocalDateTime | '' {
  if (!iso?.trim()) return '';
  const date = parseStoredUtcValue(iso);
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid UTC instant: ${iso}`);
  const parts = dateTimePartsInZone(date, timeZone);
  return `${calendarDateKeyFromParts(parts.year, parts.month, parts.day)}T${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}` as CompanyLocalDateTime;
}

/** Backward-compatible alias for instant-to-company-picker conversion. */
export function utcIsoToCompanyLocalPicker(iso: string | null | undefined): string {
  return utcInstantToCompanyPicker(iso);
}
